
<template>
  <Page>
    <div class="page">
      <Title
        title="Traversing the Campus"
        shape="circle"
        subtitle="Journey of the Autumn Fellows."
      ></Title>
      <div class="content">
        <div class="section">
          <div class="spacer"></div>
          <div class="spacer"></div>
          <div class="toc blue toc--is-shape">
            <div class="toc-header">
              <div class="toc-header-svg">
                <Shape shape="circle"></Shape>
              </div>
            </div>
            <div class="toc-content">
              <p>
                Three Fellows participated in the Autumn Fellowship from October
                17 to November 14, 2022. We divided the month into four-day
                chunks, called
                <a href="https://www.are.na/block/12181523" target="_blank"
                  >quad cycles</a
                >,to move through the phases of
                <a class="blue" href="/#orientation">Orientation</a>,
                <a class="blue" href="/#exploration">Exploration</a>, and
                <a class="blue" href="/#conclusion">Conclusion</a>. Here’s how
                the program unfolded!
              </p>
            </div>
            <div class="toc-border"></div>
          </div>
          <div class="toc-feet blue">
            <div class="toc-foot-1"></div>
            <div class="toc-foot-2"></div>
          </div>
          <div class="full-width-line">
            <div class="full-width-gradient"></div>
          </div>
          <div class="spacer"></div>
          <div class="spacer"></div>
          <div class="spacer"></div>
        </div>
        <div class="section">
          <Header first="Orientation"></Header>
          <p>
            Campus Complex Orientation was about building the “gate” that
            fellows walked through to enter the Campus. This was important
            because the Campus Complex is not an enclosed space that you can
            walk into. It’s not even a contiguous collection of buildings.
            There’s no central courtyard, admissions office, or front steps. Our
            Campus augments pre-existing spaces and is unified by our portable
            <a class="green" href="/placemaking">way-finding tools</a>, like the
            Learner Card, Compass, and Explorer Guide instead of static signage.
          </p>
          <p>
            On the first day of Orientation we assembled at
            <a
              href="https://www.are.na/teal/visiting-the-factory-afevgwt5ugs"
              target="_blank"
              >The Factory</a
            >, the Campus HQ. The Fellows received keys to the space and their
            Learner Card, which they stamped as they traversed the network. We
            facilitated a few opening exercises to enter the Campus Campus state
            of mind. One was to tell a story about…
          </p>
          <ul>
            <li>An educator who supported you on your learning journey.</li>
            <li>An educational environment you remember fondly.</li>
            <li>Something you built as a kid.</li>
          </ul>
          <!-- <Figure name="traversing/traversing-2.png" /> -->
          <p>
            In order to get in the building mood, the Fellows learned how to use
            one of the power tools at The Factory. They carved blocks of wood
            and signed them, leaving their mark in the space.
          </p>
          <Figure name="traversing/traversing-3.png" />
          <Figure name="traversing/traversing-4.png" />

          <p>
            For two Quad Cycles, we journeyed across Brooklyn and Manhattan,
            touring the seven spaces of the Campus and getting to know all of
            the stewards. Our spaces were a mix of art studios, tech offices,
            event venues, and workshops. Stewards signed up to help Fellows with
            their projects, teach them about their tools, and host classes like
            Tania's “<a
              href="https://beaconscloset.com/blogs/beacons/imaginative-upcycling-tania-lekhraj"
              target="_blank"
              >Imaginative Upcycling</a
            >.”
          </p>

          <Figure name="traversing/traversing-5.png" />
          <Figure name="traversing/traversing-6.png" />

          <Header first="Exploration"></Header>
          <p>
            The spaces we partnered with had a wide variety of tools on hand,
            including: sewing machines, 3d printers, paint canvases, power tools
            of many kinds, musical instruments, and more.
          </p>

          <Figure name="traversing/traversing-7.png" />

          <p>
            We checked in with fellows periodically during Explorer Chats, where
            they received feedforward and ideas from the organizers. The focus
            was on
            <a
              href="https://www.are.na/leave-room-for-thoughts/peer-apprenticeships"
              target="_blank"
              >Peer Apprenticeships</a
            >
            instead of the more traditional, hierarchical Mentor-Mentee
            relationship. Accountability was achieved through a shared sense of
            learning, frequent
            <a href="https://www.are.na/block/15422206" target="_blank"
              >waypoints</a
            >
            to reflect, and asynchronous visibility of Fellows’ activities. We
            also hosted a Halfwaypoint Happy Hour to bring together Campus
            explorers and peers of the Campus.
          </p>

          <Figure name="traversing/traversing-8.png" />
          <Figure name="traversing/traversing-9.png" />
          <Figure name="traversing/traversing-10.png" />

          <p>
            Some of the Fellows’ explorations included pinhole cameras, making
            music, building a daybed, and an AR etch-a-sketch that allows the
            user to draw with light.
          </p>

          <Figure name="traversing/traversing-11.png" />

          <Header first="Conclusion" />
          <p>
            To close out the program, we hosted a Peer-Educator Conference —
            like a Parent-Teacher Conference, but for fellow learners. Our
            design prompt for the event was: how can we give attendees a taste
            of what it felt like to participate in the Campus? It was less a
            formal presentation or demo day and more a continuation of the
            creative exploration. The Conference was structured around a shared
            mission: to envision new spaces for the future of Campus Complex.
            This quest was punctuated by lightning talks from the fellows about
            their experience in the program, as well as activities sprinkled
            throughout the space.
          </p>
          <Figure name="traversing/traversing-12.png" />
          <Figure name="traversing/traversing-13.png" />
          <p>
            Participants came up with ideas for new spaces, answering questions
            like: What resources would it have on offer? What would it look like
            and feel like? What would it afford and encourage? What would it be
            called? We created a work sheet for people to sketch on, based on
            the design of the Learner Cards.
          </p>
          <p>
            A colorful spectrum of Campus futures emerged: a physical learning
            garden, a pizza oven space that doubles as a kiln for firing pottery
            and glass, a midnight library for late night book clubs, a
            multi-sensory classroom for expanding your palette, a rebuild center
            for learning how to repair and upcycle.
          </p>
          <Figure name="traversing/traversing-14.png" />
          <p class="text-divider">***</p>
          <p>
            Each of the spaces that participated in the Campus Complex Autumn
            Fellowship are successful as stand-alone institutions. If each space
            was like an Overhead Projector, we were a Transparency Sheet—letting
            their light shine through but adding our own layer of educational
            programming and encouragement on top.
          </p>
          <p>
            Now that the Autumn Program has concluded, the fellows have formed
            the relationships they need to continue learning across the
            schoolscape. We're deeply inspired by everyone who participated in
            this experiment, and we're looking forward to what's next.
          </p>
          <Figure name="traversing/traversing-15.png" />
          <br />
          <br />
          <br />
          <br />
        </div>
      </div>
      <Footer />
    </div>
  </Page>
</template>

<script>
import Page from "@/components/Page.vue";
import Title from "@/components/Title.vue";
import Figure from "@/components/Figure.vue";
import Footer from "@/components/Footer.vue";
import Header from "@/components/Header.vue";
import Shape from "@/components/Shape.vue";

export default {
  name: "Traversing",
  components: {
    Page,
    Title,
    Footer,
    Header,
    Figure,
    Shape,
  },
  data() {
    return {};
  },
  computed: {},
  methods: {},
};
</script>

<style>
</style>
